import {
  isDisplayAdsetScheduleEnded,
  type AdsetWithStopSchedule,
} from "./adsetSchedule";

const DELETED = new Set(["DELETED", "TO_DELETE"]);

/** Minimal ad set row for delivery-ended gating (aligns with {@link isDisplayAdsetScheduleEnded}). */
export type AdsetLikeForDeliveryGate = AdsetWithStopSchedule & {
  status?: string | null;
};

/**
 * True when this ad set's scheduled end is in the past in the ad set's stored timezone
 * (same rules as guided-edit / {@link isDisplayAdsetScheduleEnded}; not raw `Date.parse` vs UTC now).
 */
export function hasAdsetFinishedDelivery(
  adset: AdsetWithStopSchedule | null | undefined
): boolean {
  return isDisplayAdsetScheduleEnded(adset ?? null);
}

/**
 * True when every non-deleted ad set in the list has ended delivery (per {@link hasAdsetFinishedDelivery}).
 * Used for campaign-level "edit" when the server picks a default ad set.
 */
export function everyListedAdsetFinishedDelivery(
  adsets: ReadonlyArray<AdsetLikeForDeliveryGate> | undefined | null
): boolean {
  const active = (adsets ?? []).filter(
    (a) => !DELETED.has(String(a.status ?? "").toUpperCase())
  );
  if (active.length === 0) {
    return false;
  }
  return active.every((a) => hasAdsetFinishedDelivery(a));
}

/**
 * True when at least one non-deleted ad set has ended delivery.
 * Used to block "add creative" / "add ad set" until the schedule is extended for ended ad sets.
 */
export function anyListedAdsetFinishedDelivery(
  adsets: ReadonlyArray<AdsetLikeForDeliveryGate> | undefined | null
): boolean {
  const active = (adsets ?? []).filter(
    (a) => !DELETED.has(String(a.status ?? "").toUpperCase())
  );
  return active.some((a) => hasAdsetFinishedDelivery(a));
}

/** Copy for toasts / tooltips when ad set targeting, creatives, or add flows are blocked. */
export const ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE =
  "Scheduled delivery has ended. Extend the campaign schedule to edit targeting or creatives, or to add ad sets.";
